import * as Cesium from 'cesium';
import {
	Cartesian2,
	Cartesian3,
	Entity,
	LabelCollection,
	NearFarScalar,
	PolygonHierarchy,
} from 'cesium';
import { t } from '@/i18n';
import Subscriber from './subscriber';

export default class PolygonDrawer {
	constructor(viewer, options = {}) {
		if (!viewer) throw new Error('No Cesium viewer provided');

		this._viewer = viewer;
		this._callBack = options.callback;
		this._posChange = options.posChange;
		this._options = Object.assign(
			{
				accuracy: 6,
				once: true,
				retainGeo: true,
			},
			options
		);

		this._positions = Array.isArray(options.positions) ? options.positions : [];
		this._tips = Object.assign(
			{
				leftClick: t('damages.draw.leftClick'),
				rightClick: t('damages.draw.rightClick'),
				doubleClick: t('damages.draw.doubleClick'),
				edit: t('damages.draw.edit'),
			},
			options.tips
		);

		this._pointGeometry = [];
		this._activePoint = null;
		this._polygon = null;
		this._status = 'INIT';
		this._mouseDelta = 10;
		this._lastClickPosition = new Cartesian2(Infinity, Infinity);
		this._hoveredPoint = null;

		if (!this._viewer.scene.pickPositionSupported) {
			console.warn(
				'Browser does not support pickPosition; terrain-based point selection may be incorrect.'
			);
		}

		this._labels = new LabelCollection();
		viewer.scene.primitives.add(this._labels);
		this._labels.add({
			text: 'start',
			font: 'bold 1rem Arial',
			fillColor: Cesium.Color.WHITE,
			backgroundColor: Cesium.Color.fromCssColorString('#000000'),
			backgroundPadding: new Cartesian2(4, 4),
			outlineWidth: 4,
			style: Cesium.LabelStyle.FILL_AND_OUTLINE,
			pixelOffset: new Cartesian2(4, 30),
			scale: 1,
			scaleByDistance: new NearFarScalar(1, 0.85, 8e6, 0.75),
			disableDepthTestDistance: Number.POSITIVE_INFINITY, // <--- This forces the label to be always on top
		});

		this._subscriber = new Subscriber(this._viewer);
		this._events = [];
	}

	get status() {
		return this._status;
	}

	get isDestroy() {
		return this._status === 'DESTROY';
	}

	applyCameraOffset(position) {
		if (!position) return position;

		// Kameranın pozisyonunu al
		const cameraPosition = this._viewer.camera.position;

		// Noktadan kameraya olan yön vektörünü hesapla
		const direction = Cesium.Cartesian3.subtract(cameraPosition, position, new Cesium.Cartesian3());

		// Vektörü normalize et (birim vektör yap)
		Cesium.Cartesian3.normalize(direction, direction);

		// Yönünde 0.15 birim (15 cm) ilerle.
		const offset = Cesium.Cartesian3.multiplyByScalar(direction, 0.15, new Cesium.Cartesian3());

		// Orijinal pozisyona ekle
		return Cesium.Cartesian3.add(position, offset, new Cesium.Cartesian3());
	}
	// -------------------------------------------------------------------

	updateLabel(options) {
		if (!this._viewer || this._viewer.isDestroyed() || this._labels.isDestroyed()) return;

		const label = this._labels.get(0);
		if (!label) return;

		if (!options) {
			if (!this._activePoint) {
				label.show = false;
				return;
			}
			label.show = true;
			const { leftClick, rightClick, doubleClick } = this._tips;
			let tip = `${leftClick}, ${rightClick}`;
			if (this._positions.length > 2) {
				tip = `${leftClick}, ${rightClick}, ${doubleClick}`;
			}
			label.text = tip;
			return;
		}

		const { position, text, show = true } = options;
		if (position) label.position = position;
		if (text !== undefined) label.text = text;
		label.show = show;
	}

	handlePosChange = () => {
		if (this._posChange) {
			const pos = [];
			const activeIndex = this._activePoint ? this._pointGeometry.indexOf(this._activePoint) : -1;
			this._positions.forEach((cartesian, idx) => {
				if (idx !== activeIndex) pos.push(this.cartesian2lonlat(cartesian));
			});
			this._posChange(pos);
		}
	};

	createCircleImage(color = '#87ceeb', size = 16, outlineColor = '#ffff00', outlineWidth = 2) {
		const canvas = document.createElement('canvas');
		canvas.width = size;
		canvas.height = size;

		const ctx = canvas.getContext('2d');
		const radius = size / 2;

		ctx.beginPath();
		ctx.arc(radius, radius, radius - outlineWidth / 2, 0, Math.PI * 2);
		ctx.fillStyle = outlineColor;
		ctx.fill();

		ctx.beginPath();
		ctx.arc(radius, radius, radius - outlineWidth - 1, 0, Math.PI * 2);
		ctx.fillStyle = color;
		ctx.fill();

		return canvas.toDataURL();
	}

	drawPoint(position) {
		const image = this.createCircleImage();

		const ent = this._viewer.entities.add({
			position,
			billboard: {
				image,
				verticalOrigin: Cesium.VerticalOrigin.CENTER,
				heightReference: Cesium.HeightReference.NONE,
				disableDepthTestDistance: Number.POSITIVE_INFINITY, // Ensure points are also on top
			},
		});
		this._viewer.scene.requestRender();
		return ent;
	}

	updatePointSize(entity, size) {
		if (entity.billboard) {
			const scale = size / 6;
			entity.billboard.scale = scale;
		}
	}

	onMouseClickPoint = (_, entity) => {
		if (this._status === 'EDITING' && this._activePoint) return;
		const idx = this._pointGeometry.indexOf(entity);
		this.activeOnePosition(idx);
		this._status = 'EDITING';
	};

	drawShape = (hierarchy) => {
		const poly = this._viewer.entities.add({
			id: `new-polygon-${Date.now()}`,
			polygon: {
				hierarchy: Array.isArray(hierarchy) ? new PolygonHierarchy(hierarchy) : hierarchy,
				material: Cesium.Color.YELLOW.withAlpha(0.6),
				perPositionHeight: true,
				// classificationType: Cesium.ClassificationType.CESIUM_3D_TILE, // BU KALDIRILDI
			},
		});
		this._polygon = poly;
		this._viewer.scene.requestRender();
		return poly;
	};

	handleMouseMove = (movement) => {
		const { ellipsoid } = this._viewer.scene.globe;
		if (!movement.endPosition) return;

		const scene = this._viewer.scene;
		let newPos = scene.pickPosition(movement.endPosition);
		if (!newPos) {
			newPos = this._viewer.camera.pickEllipsoid(
				movement.endPosition,
				this._viewer.scene.globe.ellipsoid
			);
		}

		if (newPos) {
			newPos = this.applyCameraOffset(newPos);
		}

		if (this._status === 'END') {
			const ent = this._viewer.scene.pick(movement.endPosition)?.id;
			if (this._hoveredPoint && this._hoveredPoint !== ent) {
				this.updatePointSize(this._hoveredPoint, 5);
				this._hoveredPoint = null;
				this.updateLabel({ show: false });
				this._viewer.canvas.style.cursor = 'default';
			}
			if (!this._hoveredPoint && this._pointGeometry.includes(ent)) {
				this._hoveredPoint = ent;
				this.updatePointSize(ent, 8);
				this.updateLabel({
					position: newPos,
					text: this._tips.edit,
					show: true,
				});
				this._viewer.canvas.style.cursor = 'pointer';
			}
			return;
		}

		if (!this._activePoint || !newPos) return;
		this._activePoint.position.setValue(newPos);
		const idx = this._pointGeometry.indexOf(this._activePoint);
		if (this._positions.length >= 2) this._positions.splice(idx, 1, newPos);
		this.updateLabel({ position: newPos });
		this._viewer.scene.requestRender();
	};

	handleMouseLeftClick = (movement) => {
		if (!movement.position || !this._activePoint) return;
		const { ellipsoid } = this._viewer.scene.globe;

		let cart =
			this._viewer.scene.pickPosition(movement.position) ||
			this._viewer.camera.pickEllipsoid(movement.position, this._viewer.scene.globe.ellipsoid);

		if (!cart || !Cesium.defined(cart)) return;
		cart = this.applyCameraOffset(cart);

		if (
			Cartesian2.magnitude(
				Cesium.Cartesian2.subtract(this._lastClickPosition, movement.position, new Cartesian2())
			) < this._mouseDelta
		)
			return;

		const idx = this._pointGeometry.indexOf(this._activePoint);
		const pGeo = this.drawPoint(cart);
		this.updatePointSize(this._activePoint, 5);
		this._activePoint = pGeo;
		this._pointGeometry.splice(idx + 1, 0, pGeo);
		this._positions.splice(idx + 1, 0, cart);
		Cartesian2.clone(movement.position, this._lastClickPosition);

		if (this._positions.length === 1) {
			this._positions.push(cart);
			const dynPos = new Cesium.CallbackProperty(
				() => new PolygonHierarchy(this._positions),
				false
			);
			this.removePolygon();
			this.drawShape(dynPos);
		}

		this.updateLabel();
		this.handlePosChange();
	};

	async loadPolygonFromCoords(coordList) {
		this.clear();

		const hasHeight = coordList.length > 0 && coordList[0].length > 2;

		if (hasHeight) {
			this._positions = coordList.map(([lon, lat, height]) =>
				Cesium.Cartesian3.fromDegrees(lon, lat, height)
			);
		} else {
			const cartographics = coordList.map(([lon, lat]) =>
				Cesium.Cartographic.fromDegrees(lon, lat)
			);

			try {
				const updatedCartographics =
					await this._viewer.scene.sampleHeightMostDetailed(cartographics);
				const HEIGHT_OFFSET = 0.05;

				this._positions = updatedCartographics.map((c) =>
					Cesium.Cartesian3.fromRadians(c.longitude, c.latitude, c.height + HEIGHT_OFFSET)
				);
			} catch (error) {
				console.warn('Could not sample height', error);
				this._positions = coordList.map(([lon, lat]) => Cesium.Cartesian3.fromDegrees(lon, lat));
			}
		}

		const dynPos = new Cesium.CallbackProperty(() => new PolygonHierarchy(this._positions), false);
		this.drawShape(dynPos);

		this._pointGeometry = this._positions.map((pos, i) => {
			const point = this.drawPoint(pos);
			this.updatePointSize(point, 5);
			this._subscriber.add(point, this.onMouseClickPoint, 'LEFT_CLICK');
			this._subscriber.add(point, this.onClickToActivateEditMode, 'LEFT_CLICK');
			return point;
		});

		this._status = 'INIT';
		this.handlePosChange();
		this._viewer.scene.requestRender();
	}

	onClickToActivateEditMode = (_, entity) => {
		const idx = this._pointGeometry.indexOf(entity);
		if (idx === -1) return;

		this._status = 'EDITING';

		this._subscriber.remove(this._pointGeometry, 'LEFT_CLICK');
		this._subscriber.removeNative(this._viewer, 'LEFT_DOUBLE_CLICK');
		this._subscriber.add(this._pointGeometry, this.onMouseClickPoint, 'LEFT_CLICK');

		if (this._events.length === 0) {
			this._events = [
				this._subscriber.addExternal(this.handleMouseLeftClick, 'LEFT_CLICK'),
				this._subscriber.addExternal(this.handleMouseMove, 'MOUSE_MOVE'),
				this._subscriber.addExternal(this.handleMouseRightClick, 'RIGHT_CLICK'),
				this._subscriber.addExternal(this.endDraw, 'LEFT_DOUBLE_CLICK'),
			];
		}

		this.activeOnePosition(idx);
	};

	addOnePosition = (index, pos) => {
		if (!Array.isArray(pos)) return false;
		const cart = Cartesian3.fromDegrees(pos[0], pos[1]);
		const geo = this.drawPoint(cart);
		this.updatePointSize(geo, 5);
		this._subscriber.add(geo, this.onMouseClickPoint, 'LEFT_CLICK');
		this._positions.splice(index, 0, cart);
		this._pointGeometry.splice(index, 0, geo);
		this.handlePosChange();
		this._viewer.scene.requestRender();
		return true;
	};

	changeOnePosition = (index, pos) => {
		if (!Array.isArray(pos)) return false;
		const cart = Cartesian3.fromDegrees(pos[0], pos[1]);
		this._positions[index] = cart;
		this._pointGeometry[index].position.setValue(cart);
		return true;
	};

	removeOnePosition = (index) => {
		if (this._positions.length <= 2) return false;
		const geo = this._pointGeometry[index];
		if (this._status === 'EDITING') this._subscriber.remove(geo, 'LEFT_CLICK');
		this._viewer.entities.remove(geo);
		this._positions.splice(index, 1);
		this._pointGeometry.splice(index, 1);
		this.handlePosChange();
		this._viewer.scene.requestRender();
		return true;
	};

	activeOnePosition = (index) => {
		const geo = this._pointGeometry[index];
		this._activePoint = geo;
		this.updatePointSize(geo, 8);
		this.updateLabel();
		this._viewer.scene.requestRender();
	};

	handleMouseRightClick = () => {
		if (!this._activePoint) return;
		const idx = this._pointGeometry.indexOf(this._activePoint);
		if (idx <= 0) return;
		this.removeOnePosition(idx - 1);
		this.updateLabel();
	};

	start() {
		this.stop();
		this._pointGeometry = [];
		const initPos = this._positions[1] || new Cartesian3();
		const first = this.drawPoint(initPos);
		this._positions.length = 0;
		this._pointGeometry.push(first);
		this._activePoint = first;
		this._subscriber.removeNative(this._viewer, 'LEFT_DOUBLE_CLICK');

		if (this._events.length === 0) {
			console.log('Adding events for PolygonDrawer');
			this._events = [
				this._subscriber.addExternal(this.handleMouseLeftClick, 'LEFT_CLICK'),
				this._subscriber.addExternal(this.handleMouseMove, 'MOUSE_MOVE'),
				this._subscriber.addExternal(this.handleMouseRightClick, 'RIGHT_CLICK'),
				this._subscriber.addExternal(this.endDraw, 'LEFT_DOUBLE_CLICK'),
			];
		}

		this.updateLabel();
		this._status = 'START';
		this.handlePosChange();
	}

	removePolygon = () => {
		if (!this._viewer || this._viewer.isDestroyed()) return;

		if (this._polygon) {
			this._viewer.entities.remove(this._polygon);
			this._polygon = null;
		}
	};

	stop(removePoint = true) {
		const isViewerValid = this._viewer && !this._viewer.isDestroyed();

		if (removePoint) {
			this._subscriber.remove(this._pointGeometry, 'LEFT_CLICK');
			if (isViewerValid) {
				this._pointGeometry.forEach((ent) => this._viewer.entities.remove(ent));
			}
			this._pointGeometry = [];
		}
		if (this._activePoint) {
			const idx = this._pointGeometry.indexOf(this._activePoint);
			if (isViewerValid) {
				this._viewer.entities.remove(this._activePoint);
			}
			this._pointGeometry.splice(idx, 1);
			this._activePoint = null;
		}
		this._status = 'END';
		this._lastClickPosition = new Cartesian2(Infinity, Infinity);
		if (isViewerValid) {
			this._viewer.scene.requestRender();
		}
		return true;
	}

	cartesian2lonlat(cart) {
		if (!cart) return [];
		const accuracy = this._options.accuracy;
		const carto = this._viewer.scene.globe.ellipsoid.cartesianToCartographic(cart);

		const lon = +Cesium.Math.toDegrees(carto.longitude).toFixed(accuracy);
		const lat = +Cesium.Math.toDegrees(carto.latitude).toFixed(accuracy);
		const height = +carto.height.toFixed(2);

		return [lon, lat, height];
	}

	endDraw = () => {
		if (!this._activePoint) return false;

		const idx = this._pointGeometry.indexOf(this._activePoint);
		this._positions.splice(idx, 1);

		if (this._positions.length < 3) return false;
		if (!this._options.once) this.start();
		else this.stop(false);

		if (!this._options.retainGeo) this.removePolygon();

		this._subscriber.add(this._pointGeometry, this.onMouseClickPoint, 'LEFT_CLICK');
		if (this._callBack) {
			this._callBack(this._positions.map((cart) => this.cartesian2lonlat(cart)));
		}

		this.updateLabel();
		return true;
	};

	clear() {
		this.stop();
		this.removePolygon();
		this.updateLabel();
	}

	destory() {
		this._subscriber.removeExternal(this._events);
		this._events = [];
		this.clear();
		if (this._viewer && !this._viewer.isDestroyed() && !this._labels.isDestroyed()) {
			this._labels.destroy();
		}
		this._subscriber.destroy();
		this._status = 'DESTROY';
	}
}
